// eslint global TWEEN: false
import { merge, forOwn, each } from 'lodash';
import { WebGLRenderer,
  PerspectiveCamera,
  TextureLoader,
  Mesh,
  SphereGeometry,
  PlaneGeometry,
  MeshBasicMaterial,
  Object3D,
  Scene,
  DirectionalLight,
  Vector3,
  Geometry,
  CubicBezierCurve3,
  Color,
  BackSide,
  Vector2,
  AdditiveBlending,
  ShaderMaterial,
} from 'three';

import { MeshLine, MeshLineMaterial } from 'three.meshline';
import OrbitControls from './OrbitControls';
import utils from './GlobeUtils';

export default class FlyGlobe {
  constructor(config = { }) {
    const defaultConfig = {
      SELECTOR: '.globe__3d-container',
      TEXTURES: {
        EARTH: '/static/earth_square_light.jpg',
        STARFIELD: '/static/globe_background.jpg',
        MARKER_SELECTED: '/static/selected_marker.png',
        MARKER_UNSELECTED: '/static/unselected_marker.png',
        SURREY_LABEL: '/static/surrey-label.png',
      },
      COLORS: {
        BASE: new Color(0xb5b5b5),
        ACTIVE: new Color('rgb(255, 166, 18)'),
      },
      EARTH: {
        RADIUS: 50,
      },
      HOME_COORDS: {
        CAMERA: {
          LAT: 46.111326,
          LON: -80.859375,
        },
        CAMERA_START: {
          LAT: -48.385442,
          LON: 79.804688,
        },
        ORIGIN_MARKER: {
          LAT: 49.183333,
          LON: -122.85000000000002,
        },
        LABEL: {
          LAT: 43.5,
          LON: -135,
        },
      },
    };
    this.config = merge(config, defaultConfig);
    this.meshMap = {};
    this.textureMap = {};
    this.lightMap = {};
    this.activeFlightPath = null;
    this.introComplete = false;
  }

  init(links) {
    return this.initTextures()
      .then(() => {
        this.initView();
        this.initCamera();
        this.initGlobe();
        this.initStars();
        this.initAtmosphere();
        this.initLights();
        this.initMarkers(links);
        this.initLabel();
        this.initScene();

        this.resetCamera();
        this.initFlightPaths(links);
        this.initControls();
        this.onResize();
        this.loop();
        this.onResizeHandler = this.onResize.bind(this);
        window.addEventListener('resize', this.onResizeHandler, false);
      });
  }

  initTextures() {
    const loader = new TextureLoader();
    return new Promise((resolve, reject) => {
      each(this.config.TEXTURES, (url, id) => {
        loader.load(
          url,
          (datum) => {
            this.textureMap[id] = datum;
            if (Object.keys(this.textureMap).length === Object.keys(this.config.TEXTURES).length) {
              resolve(this.textureMap);
            }
          },
          null,
          () => {
            reject();
          },
        );
      });
    });
  }

  initView() {
    this.view = document.querySelector(this.config.SELECTOR);
    this.renderer = new WebGLRenderer({
      alpha: true,
      canvas: document.querySelector('.globe__canvas'),
    });
    this.renderer.setPixelRatio(2);
    this.renderer.setClearColor(0xFFFFFF, 0);
    this.view.appendChild(this.renderer.domElement);
  }

  initControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enabled = true;
    this.controls.enableZoom = false;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.15;
    this.controls.rotateSpeed = 0.5;
    this.controls.enablePan = false;
  }

  initCamera() {
    this.camera = new PerspectiveCamera(
      window.innerWidth <= 600 ? 80 : 50,
      window.innerWidth / window.innerHeight,
      1,
      10000,
    );
    this.camera.lookAt(0, 0, 0);
  }

  initGlobe() {
    const geo = new SphereGeometry(this.config.EARTH.RADIUS, 48, 48);
    const texture = new MeshBasicMaterial({
      map: this.textureMap.EARTH,
      opacity: 0,
      transparent: true,
    });
    this.meshMap.globe = new Mesh(geo, texture);
  }

  initStars() {
    const geo = new SphereGeometry(this.config.EARTH.RADIUS * 2.5, 48, 48);
    const texture = new MeshBasicMaterial({
      map: this.textureMap.STARFIELD,
      opacity: 0,
      transparent: true,
    });
    texture.side = BackSide;
    this.meshMap.stars = new Mesh(geo, texture);
  }

  initAtmosphere() {
    const color = 0x05181f;
    const intensity = 0;
    const fade = 3;
    const glowMaterial = new ShaderMaterial({
      uniforms: {
        c: {
          type: 'f',
          value: intensity,
        },
        p: {
          type: 'f',
          value: fade,
        },
        glowColor: {
          type: 'c',
          value: new Color(color),
        },
        viewVector: {
          type: 'v3',
          value: this.camera.position,
        },
      },
      vertexShader: `
        uniform vec3 viewVector;
        uniform float c;
        uniform float p;
        varying float intensity;
        void main() {
          vec3 vNormal = normalize( normalMatrix * normal );
          vec3 vNormel = normalize( normalMatrix * viewVector );
          intensity = pow( c - dot(vNormal, vNormel), p );
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }`,
      fragmentShader: `
        uniform vec3 glowColor;
        varying float intensity;
        void main() 
        {
          vec3 glow = glowColor * intensity;
          gl_FragColor = vec4( glow, 1.0 );
        }`,
      side: BackSide,
      blending: AdditiveBlending,
      transparent: true,
      opacity: 0.9,
    });
    const geo = new SphereGeometry(this.config.EARTH.RADIUS + 10, 48, 48);
    this.meshMap.atmosphere = new Mesh(geo, glowMaterial);
  }

  initMarkers(links = []) {
    this.meshMap.baseMarkers = {};
    links.forEach(link => this.initBaseMarker(link));
    const geo = new PlaneGeometry(4, 4, 4, 4);
    const texture = new MeshBasicMaterial({
      map: this.textureMap.MARKER_SELECTED,
      alphaTest: 0.5,
      opacity: 0,
    });
    this.meshMap.markers = {
      origin: new Mesh(geo, texture),
      destination: new Mesh(geo, texture),
    };
  }

  initLabel() {
    const geo = new PlaneGeometry(14, 14, 2, 2);
    const texture = new MeshBasicMaterial({
      map: this.textureMap.SURREY_LABEL,
      alphaTest: 0.5,
      opacity: 0,
    });
    this.meshMap.surreyLabel = new Mesh(geo, texture);
  }

  initFlightPaths(links = []) {
    this.meshMap.flightPaths = {};
    links.forEach(link => this.initFlightPath(link));
    forOwn(this.meshMap.flightPaths, (value) => {
      this.scene.add(value);
    });
  }

  initBaseMarker(link) {
    const geo = new PlaneGeometry(1, 1, 1, 1);
    const texture = new MeshBasicMaterial({
      map: this.textureMap.MARKER_UNSELECTED,
      transparent: true,
      opacity: 0,
    });

    this.meshMap.baseMarkers[link.slug()] = new Mesh(geo, texture);
    this.setMarkerPosition(this.meshMap.baseMarkers[link.slug()], link.lat(), link.lon(), 2);
  }

  initLights() {
    this.lightMap.main = new DirectionalLight(0xcccccc);
    this.lightMap.main.position.set(0, 0, 0);
  }

  initScene() {
    const obj = new Object3D();
    obj.add(this.meshMap.globe);
    obj.add(this.meshMap.stars);
    obj.add(this.meshMap.atmosphere);
    this.scene = new Scene();
    this.scene.add(obj);
    this.scene.add(this.lightMap.main);
    forOwn(this.meshMap.markers, (value) => {
      this.scene.add(value);
    });

    forOwn(this.meshMap.baseMarkers, (value) => {
      this.scene.add(value);
    });

    this.scene.add(this.meshMap.surreyLabel);

    this.setMarkerPosition(
      this.meshMap.markers.origin,
      this.config.HOME_COORDS.ORIGIN_MARKER.LAT,
      this.config.HOME_COORDS.ORIGIN_MARKER.LON,
    );

    this.setMarkerPosition(
      this.meshMap.surreyLabel,
      this.config.HOME_COORDS.LABEL.LAT,
      this.config.HOME_COORDS.LABEL.LON,
      4,
    );
  }

  destroy() {
    this.destroyed = true;
    window.removeEventListener('resize', this.onResizeHandler, false);
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.renderer.domElement.addEventListener('dblclick', null, false);
    this.scene = null;
    this.camera = null;
    this.controls = null;
  }

  resetCamera() {
    const homeLat = this.config.HOME_COORDS.CAMERA_START.LAT;
    const homeLon = this.config.HOME_COORDS.CAMERA_START.LON;
    this.setCameraPosition(homeLat, homeLon);
  }

  setCameraPosition(lat, lon) {
    this.currentCameraPos = {
      lat,
      lon,
    };
    const pos = this.posOnGlobe(-100, lat, lon);
    this.camera.position.copy(pos);
    this.camera.lookAt(this.scene.position);
  }

  outroAnimation() {
    const self = this;
    const current = {
      opacity: 1,
      zoom: 1,
    };
    this.animateCameraTo(
      this.config.HOME_COORDS.CAMERA.LAT,
      this.config.HOME_COORDS.CAMERA.LON,
      900,
    );
    new TWEEN.Tween(current)
      .to({ opacity: 0, zoom: 4 }, 900)
      .easing(TWEEN.Easing.Quintic.InOut)
      .onUpdate(function fadeOut() {
        self.meshMap.globe.material.opacity = this.opacity;
        self.meshMap.stars.material.opacity = this.opacity;
        self.meshMap.markers.origin.material.opacity = this.opacity;
        self.meshMap.surreyLabel.material.opacity = this.opacity;
        self.meshMap.atmosphere.material.uniforms.c.value = this.opacity * 0.7;
        self.camera.zoom = this.zoom;
        each(self.meshMap.baseMarkers, (value, key) => {
          const marker = value;
          const flightPath = self.meshMap.flightPaths[key];
          marker.material.opacity = this.opacity;
          flightPath.material.uniforms.opacity.value = this.opacity / 10;
          flightPath.material.uniforms.needsUpdate = true;
          if (self.activeFlightPath) {
            self.activeFlightPath.active.material.uniforms.opacity.value = this.opacity;
            self.activeFlightPath.active.material.uniforms.needsUpdate = true;
          }
        });
      })
      .onComplete(() => {
        this.destroy();
      })
      .start();
  }

  quickIntroAnimation(lat = this.config.HOME_COORDS.CAMERA.LAT, lon = this.config.HOME_COORDS.CAMERA.LON) { // eslint-disable-line
    const self = this;
    return new Promise((resolve) => {
      const current = {
        opacity: 0,
        zoom: 4,
      };
      new TWEEN.Tween(current)
        .to({ opacity: 1, zoom: 1 }, 1000)
        .easing(TWEEN.Easing.Quintic.InOut)
        .onUpdate(function fade() {
          self.meshMap.globe.material.opacity = this.opacity;
          self.meshMap.stars.material.opacity = this.opacity;
          self.meshMap.atmosphere.material.uniforms.c.value = this.opacity * 0.7;
          self.camera.zoom = this.zoom;
        })
        .onComplete(() => {
          this.introComplete = true;
          resolve();
        })
        .start();
      this.animateSurreyIn(500, 500);
      this.animateMarkersAndLinesIn(500, 500);
      this.animateCameraTo(
        lat,
        lon,
        1000,
      );
    });
  }

  introAnimation() {
    const self = this;
    const current = {
      opacity: 1,
      zoom: 4,
    };
    this.camera.zoom = 4;
    return new Promise((resolve) => {
      new TWEEN.Tween(current)
        .to({ opacity: 1, zoom: 1 }, 1000)
        .easing(TWEEN.Easing.Quintic.InOut)
        .onUpdate(function fade() {
          self.meshMap.globe.material.opacity = this.opacity;
          self.meshMap.stars.material.opacity = this.opacity;
          self.meshMap.atmosphere.material.uniforms.c.value = this.opacity * 0.7;
          self.camera.zoom = this.zoom;
        })
        .start();
      this.animateCameraTo(
        self.config.HOME_COORDS.CAMERA.LAT,
        self.config.HOME_COORDS.CAMERA.LON,
        1500,
        );
      this.animateSurreyIn();
      this.animateMarkersAndLinesIn()
        .then(() => {
          this.introComplete = true;
          resolve();
        });
    });
  }

  animateSurreyIn(animationTime = 500, delay = 1000) {
    const self = this;
    return new Promise((resolve) => {
      new TWEEN.Tween({ opacity: 0 })
        .delay(delay)
        .to({ opacity: 1 }, animationTime)
        .easing(TWEEN.Easing.Quintic.InOut)
        .onUpdate(function update() {
          self.meshMap.markers.origin.material.opacity = this.opacity;
          self.meshMap.surreyLabel.material.opacity = this.opacity;
        })
        .onComplete(resolve)
        .start();
    });
  }

  animateMarkersAndLinesIn(animationTime = 500, delayStart = 1000) {
    return new Promise((resolve) => {
      let finishedCount = 0;
      const totalCount = Object.keys(this.meshMap.baseMarkers).length;
      each(this.meshMap.baseMarkers, (value, key) => {
        const marker = value;
        const flightPath = this.meshMap.flightPaths[key];
        new TWEEN.Tween({ opacity: 0 })
          .delay(delayStart + Math.random() * animationTime) // eslint-disable-line
          .to({ opacity: 1 }, animationTime)
          .easing(TWEEN.Easing.Quintic.InOut)
          .onUpdate(function update() {
            marker.material.opacity = this.opacity;
            flightPath.material.uniforms.opacity.value = this.opacity / 10;
            flightPath.material.uniforms.needsUpdate = true;
          })
          .onComplete(() => {
            finishedCount += 1;
            if (finishedCount === totalCount) {
              resolve();
            }
          })
          .start();
      });
    });
  }

  setMarkerPosition(marker, lat, lon, radius = 2) {
    const pos = this.latLonToSphere(lat, lon, this.config.EARTH.RADIUS + radius);
    marker.position.copy(pos);
  }

  setDestination(link) {
    return new Promise((resolve) => {
      const startLat = this.config.HOME_COORDS.ORIGIN_MARKER.LAT;
      const startLon = this.config.HOME_COORDS.ORIGIN_MARKER.LON;
      const newCameraPos = link.cameraPos().lat && link.cameraPos().lon ?
        link.cameraPos() :
        utils.midPoint(startLat, startLon, link.lat(), link.lon());
      let promise = null;
      if (this.introComplete === true) {
        this.animateMarkerOut(link.slug());
        promise = this.animateCameraTo(newCameraPos.lat, newCameraPos.lon, 500);
      } else {
        promise = this.quickIntroAnimation(newCameraPos.lat, newCameraPos.lon, 1000);
      }
      promise.then(() => {
        this.animateDestinationTo(link.lat(), link.lon());
        this.setFlightPath(link.slug());
        resolve();
      });
    });
  }

  animateMarkerOut(slug) {
    if (this.activeBaseMarker) {
      const oldMarker = this.activeBaseMarker;
      new TWEEN.Tween({ scale: 0.1 })
        .to({ scale: 1 }, 250)
        .easing(TWEEN.Easing.Quintic.In)
        .onUpdate(function scale() {
          oldMarker.scale.set(this.scale, this.scale, this.scale);
        })
        .start();
    }
    const marker = this.meshMap.baseMarkers[slug];
    new TWEEN.Tween({ scale: 1 })
      .to({ scale: 0.1 }, 250)
      .easing(TWEEN.Easing.Quintic.In)
      .onUpdate(function scale() {
        marker.scale.set(this.scale, this.scale, this.scale);
      })
      .onComplete(() => {
        this.activeBaseMarker = marker;
      })
      .start();
  }

  initFlightPath(link) {
    const startLat = this.config.HOME_COORDS.ORIGIN_MARKER.LAT;
    const startLon = this.config.HOME_COORDS.ORIGIN_MARKER.LON;
    const startPos = this.posOnGlobe(-2, startLat, startLon);
    const endPos = this.posOnGlobe(-2, link.lat(), link.lon());
    const controlToPos = endPos.clone();
    const controlFromPos = startPos.clone();
    const xC = (0.5 * (startPos.x + endPos.x));
    const yC = (0.5 * (startPos.y + endPos.y));
    const zC = (0.5 * (startPos.z + endPos.z));
    const dist = endPos.distanceTo(startPos);
    const midPos = new Vector3(xC, yC, zC);
    const smoothDist = utils.map(dist, 0, 100, 1.05, 1.4);
    let radius = 49;
    if (link.slug() === 'paris') {
      radius = 50;
    }
    midPos.setLength(radius * smoothDist);
    controlToPos.add(midPos);
    controlFromPos.add(midPos);
    controlToPos.setLength(radius * smoothDist);
    controlFromPos.setLength(radius * smoothDist);
    const curve = new CubicBezierCurve3(startPos, controlFromPos, controlToPos, endPos);
    const geometry = new Geometry();
    geometry.vertices = curve.getPoints(20);
    const resolution = new Vector2(window.innerWidth, window.innerHeight);
    const material = new MeshLineMaterial({
      color: this.config.COLORS.ACTIVE,
      transparent: true,
      sizeAttenuation: 1,
      lineWidth: 0.3,
      resolution,
      near: this.camera.near,
      far: this.camera.far,
      depthTest: true,
      repeat: new Vector2(1, 2),
      opacity: 0,
    });
    const activeMaterial = new MeshLineMaterial({
      color: this.config.COLORS.ACTIVE,
      transparent: true,
      sizeAttenuation: 1,
      lineWidth: 0.4,
      resolution,
      near: this.camera.near,
      far: this.camera.far,
      depthTest: true,
      repeat: new Vector2(1, 2),
      opacity: 0,
    });
    const line = new MeshLine();
    const lineActive = new MeshLine();
    line.setGeometry(geometry);
    lineActive.setGeometry(geometry);
    this.meshMap.flightPaths[link.slug()] = new Mesh(line.geometry, material);
    this.meshMap.flightPaths[`${link.slug()}-active`] = new Mesh(lineActive.geometry, activeMaterial);
  }

  setFlightPath(slug) {
    if (this.activeFlightPath) {
      this.animateFlightPath(this.activeFlightPath.base, 0.1);
      this.animateFlightPath(this.activeFlightPath.active, 0);
    }
    this.activeFlightPath = { base: this.meshMap.flightPaths[slug], active: this.meshMap.flightPaths[`${slug}-active`] };
    this.animateFlightPath(this.activeFlightPath.base, 0);
    this.animateFlightPath(this.activeFlightPath.active, 1);
  }

  animateFlightPath(path, opacity) { // eslint-disable-line class-methods-use-this
    const toPath = path.material;
    return new Promise((resolve) => {
      new TWEEN.Tween({ opacity: toPath.uniforms.opacity.value }) // eslint-disable-line
        .to({ opacity }, 250)
        .easing(TWEEN.Easing.Quintic.In)
        .onUpdate(function updatePath() {
          toPath.uniforms.opacity.value = this.opacity;
          toPath.uniforms.needsUpdate = true;
        })
        .onComplete(() => {
          resolve();
        })
        .start();
    });
  }

  animateDestinationTo(lat, lon) {
    const marker = this.meshMap.markers.destination;
    new TWEEN.Tween({ scale: 1 })
      .to({ scale: 0.1 }, 250)
      .easing(TWEEN.Easing.Quintic.In)
      .onUpdate(function scale() {
        marker.scale.set(this.scale, this.scale, this.scale);
      })
      .onComplete(() => {
        this.setMarkerPosition(this.meshMap.markers.destination, lat, lon);
        new TWEEN.Tween({ scale: 0.1 })
          .to({ scale: 1 }, 250)
          .easing(TWEEN.Easing.Quintic.Out)
          .onUpdate(function scale() {
            marker.scale.set(this.scale, this.scale, this.scale);
          })
          .start();
      })
      .start();
  }

  animateCameraTo(lat, lon, duration = 500) {
    const from = this.sphereToLatLon(this.localPos(100, this.camera.position));
    const to = { lat, lon };
    const self = this;
    return new Promise((resolve) => {
      new TWEEN.Tween(from)
        .to(to, duration)
        .easing(TWEEN.Easing.Quintic.InOut)
        .onUpdate(function move() {
          const newPos = self.cameraPos(-100, self.latLonToSphere(this.lat, this.lon));
          self.camera.position.copy(newPos);
        })
        .onComplete(resolve)
        .start();
    });
  }

  loop() {
    if (!this.destroyed) {
      TWEEN.update();
      forOwn(this.meshMap.markers, (value) => {
        if (!this.destroyed) {
          if (this.camera) {
            value.rotation.setFromRotationMatrix(this.camera.matrix);
          }
        }
      });
      forOwn(this.meshMap.baseMarkers, (value) => {
        if (this.camera) {
          value.rotation.setFromRotationMatrix(this.camera.matrix);
        }
      });

      if (this.camera) {
        this.meshMap.surreyLabel.rotation.setFromRotationMatrix(this.camera.matrix);
      }
      this.animationFrame = requestAnimationFrame(this.loop.bind(this));

      if (this.controls) {
        this.controls.update();
      }
      if (this.camera) {
        this.camera.updateProjectionMatrix();
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
      }
    }
  }

  latLonToSphere(lat, lon, sphereRadius = this.config.EARTH.RADIUS) {
    const phi = (90 - lat) * (Math.PI / 180);
    const theta = (lon + 180) * (Math.PI / 180);
    const x = -((sphereRadius) * Math.sin(phi) * Math.cos(theta));
    const z = ((sphereRadius) * Math.sin(phi) * Math.sin(theta));
    const y = ((sphereRadius) * Math.cos(phi));
    return new Vector3(x, y, z);
  }

  sphereToLatLon(vector3, sphereRadius = this.config.EARTH.RADIUS) {
    const lat = 90 - (Math.acos(vector3.y / sphereRadius)) * 180 / Math.PI; // eslint-disable-line
    const lon = ((270 + (Math.atan2(vector3.x , vector3.z)) * 180 / Math.PI) % 360) -360; // eslint-disable-line
    return {
      lat,
      lon,
    };
  }

  posOnGlobe(zoom, lat, lon) {
    const pos = this.latLonToSphere(lat, lon);
    return this.cameraPos(zoom, pos);
  }

  localPos(zoom, pos) {
    const posObj = new Object3D();
    posObj.position.copy(pos);
    posObj.lookAt(this.scene.position);
    posObj.translateZ(zoom);
    return posObj.position;
  }

  cameraPos(zoom, pos) {
    const posObj = new Object3D();
    posObj.position.copy(pos);
    posObj.lookAt(this.scene.position);
    posObj.translateZ(zoom);
    return posObj.position;
  }

  onResize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (this.camera && this.camera.aspect && this.renderer) {
      this.camera.aspect = width / height;
      this.renderer.setSize(width, height);
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/FlyGlobe.js